import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranslation } from 'react-i18next';
import type { BugEvaluationDetail } from '../../../lib/bug-evaluation-api';
import { TranslatedBadge } from './TranslatedBadge';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価詳細ページのバグ詳細セクション。チケットタイトル / 概要 / 期待 vs 実際 / 原因 / 再現手順 / 対応策 / 関連メタを描画する。
 * @graph-connects TranslatedBadge [calls] 翻訳済みバッジ
 */
export function BugDetailsCard({
  detail,
  isTranslated,
  showOriginal,
}: {
  detail: BugEvaluationDetail;
  isTranslated: boolean;
  showOriginal: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card className="p-6">
      <div className="flex items-center mb-4">
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
          {t('bugEvaluation.bugDetails', 'バグ詳細')}
        </h2>
        {isTranslated && !showOriginal && <TranslatedBadge />}
      </div>
      <div className="space-y-4 text-sm">
        {detail.issueSummary && (
          <div>
            <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.ticketTitle', 'チケットタイトル')}
            </div>
            <div>{detail.issueSummary}</div>
          </div>
        )}
        {detail.bugSummary && (
          <div>
            <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.summary', '概要')}
            </div>
            <div className="whitespace-pre-wrap">{detail.bugSummary}</div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {detail.expectedResult && (
            <div>
              <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                {t('bugEvaluation.expectedResult', '期待した結果')}
              </div>
              <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3 whitespace-pre-wrap">
                {detail.expectedResult}
              </div>
            </div>
          )}
          {detail.actualResult && (
            <div>
              <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                {t('bugEvaluation.actualResult', '実際の結果')}
              </div>
              <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3 whitespace-pre-wrap">
                {detail.actualResult}
              </div>
            </div>
          )}
        </div>
        {detail.bugReason && (
          <div>
            <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.cause', '原因')}
            </div>
            <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3 whitespace-pre-wrap">
              {detail.bugReason}
            </div>
          </div>
        )}
        {detail.stepsToReproduce && (
          <div>
            <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.stepsToReproduce', '再現手順')}
            </div>
            <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3 whitespace-pre-wrap">
              {detail.stepsToReproduce}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {detail.workaroundPlan && (
            <div>
              <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                {t('bugEvaluation.workaroundPlan', '暫定対応策')}
              </div>
              <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3 whitespace-pre-wrap">
                {detail.workaroundPlan}
              </div>
            </div>
          )}
          {detail.permanentSolution && (
            <div>
              <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                {t('bugEvaluation.permanentSolution', '恒久対応策')}
              </div>
              <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3 whitespace-pre-wrap">
                {detail.permanentSolution}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-[var(--color-text-muted)]">
          {detail.relatedIssueKey && (
            <span>
              {t('bugEvaluation.relatedIssue', '関連案件')}: {detail.relatedIssueKey}
            </span>
          )}
          {detail.causeProjectKey && (
            <span>
              {t('bugEvaluation.causeProject', '起因案件')}: {detail.causeProjectKey}
            </span>
          )}
          {detail.service && (
            <span>
              {t('bugEvaluation.service', 'サービス')}: {detail.service}
            </span>
          )}
          {detail.incidentLevel && (
            <span>
              {t('bugEvaluation.level', 'レベル')}: {detail.incidentLevel}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
